import React, { useEffect, useState } from 'react';
import Input from 'components/Input';
import { PasswordForm, PasswordHint } from './styles';
import Icon from 'components/Icon';
import Button from '../../../components/Button/Button';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import {
  makhsanSelfUserInfo,
  makhsanUpdateUser,
  IUserInfo,
  makhsanUpdateUserFn,
  makhsanSelfUserInfoFn,
} from 'store/makhsanUserInfo/makhsanUserInfoSlice';
import { LoadingIcon } from '../../../components/LoadingIcon/LoadingIcon';
import notify from '../../../components/Notify';
import {
  HintIcon,
  HintText,
  QuestionHintBox,
} from '../../../components/FileManager/ItemsViewer/Survey/SurveyEditor/GeneralQuestion/styles';
import { black, blue, red } from '../../../utils/style/colors';
import { appDispatch, RootState } from 'store/store';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import i18n from '../../../i18n';
import LocalIcon from 'ui/LocalIcon';

interface IOwnProps {
  changedHeight?: () => void;
}

interface IStateToProps {
  userInfo?: IUserInfo;
  thisUser: IThisUserState;
}

interface IDispatchToProps {
  updateUser: (
    deleted: boolean | null,
    firstName: string | null,
    lastName: string | null,
    password: string | null,
    active2FA: boolean | null,
    enable: boolean | null,
    userId: number
  ) => ReturnType<typeof makhsanUpdateUserFn>;
  selfUserInfo: (userId: number) => ReturnType<typeof makhsanSelfUserInfoFn>;
}

type IComponentProps = IStateToProps & IDispatchToProps & IOwnProps;
const ChangePassword: React.FunctionComponent<IComponentProps> = ({
  updateUser,
  userInfo,
  selfUserInfo,
  thisUser,
  changedHeight,
}) => {
  const [password1, setPassword1] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [password2, setPassword2] = useState<string>('');
  const [changing, setChanging] = useState<boolean>(false);
  const [changing2FA, setChanging2FA] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showRepeatedPassowrd, setShowRepeatedPassowrd] =
    useState<boolean>(false);

  useEffect(() => {
    if (!userInfo) {
      selfUserInfo(Number(thisUser.userId));
    }
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    if (!userInfo) return;
    updateUser(false, null, null, password1, null, null, userInfo.user_id).then(
      () => {
        setLoading(false);
        setChanging(false);
        setPassword1('');
        setPassword2('');
        notify(i18n.t('passwordChanged', { ns: 'profile' }));
      },
      (e: any) => {
        setLoading(false);
        notify(e.data.message);
      }
    );
  };
  const handleChange2FA = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    if (!userInfo) return;
    const new2fa = !userInfo.active2fa;
    updateUser(
      false,
      null,
      null,
      password1,
      new2fa,
      null,
      userInfo.user_id
    ).then(
      () => {
        setLoading(false);
        setChanging2FA(false);
        setPassword1('');
        notify(
          new2fa
            ? i18n.t('the2faActivated', { ns: 'profile' })
            : i18n.t('the2faDeactivated', { ns: 'profile' })
        );
      },
      (e: any) => {
        setLoading(false);
        notify(e.data.message);
      }
    );
  };

  const handleTogglePassword = (
    password: 'password' | 'reapeated_password'
  ) => {
    if (password === 'password') {
      setShowPassword((prevState) => !prevState);
    } else {
      setShowRepeatedPassowrd((prevState) => !prevState);
    }
  };

  const hasLowerCase = password1.match('.*[a-z]+.*') !== null;
  const has8chars = password1.length >= 8;
  const hasUpperCase = password1.match('.*[A-Z]+.*') !== null;
  const hasDigit = password1.match('.*[0-9]+.*') !== null;
  const hasSpecial = password1.match('.*[~!@#$%^&*()\\-=+?:]+.*') !== null;
  const matches = password1 === password2;
  const allowSubmit =
    hasLowerCase &&
    has8chars &&
    hasUpperCase &&
    hasDigit &&
    hasSpecial &&
    matches;
  return !userInfo ? (
    <LoadingIcon />
  ) : changing ? (
    <PasswordForm onSubmit={handleSubmit}>
      <input
        type="text"
        name="username"
        value={userInfo.phone?.replace(/^0098/, '0') || userInfo.email}
        autoComplete="username"
        style={{ position: 'absolute', left: '-9999px' }}
      />
      <h6> {i18n.t('changePassword', { ns: 'profile' }) as string}</h6>
      <div className="relative">
        <Input
          id="password1"
          type={showPassword ? 'text' : 'password'}
          name="password1"
          required={true}
          placeholder={i18n.t('password', { ns: 'profile' })}
          autoComplete="new-password"
          value={password1}
          onChange={(e) => setPassword1(e.target.value)}
        />
        <LocalIcon
          iconName={showPassword ? 'eye-off.svg' : 'eye.svg'}
          color={black}
          type="sm"
          style={{
            marginInlineStart: '0.5rem',
            cursor: 'pointer',
            position: 'absolute',
            left: '0.25rem',
            top: showPassword ? '0.7rem' : '1rem',
          }}
          onClick={() => handleTogglePassword('password')}
        />
      </div>
      <PasswordHint matches={has8chars}>
        {i18n.t('has8chars', { ns: 'profile' }) as string}
      </PasswordHint>
      <PasswordHint matches={hasLowerCase}>
        {i18n.t('hasLowerCase', { ns: 'profile' }) as string}
      </PasswordHint>
      <PasswordHint matches={hasUpperCase}>
        {i18n.t('hasUpperCase', { ns: 'profile' }) as string}
      </PasswordHint>
      <PasswordHint matches={hasDigit}>
        {i18n.t('passwordChanged', { ns: 'profile' }) as string}
      </PasswordHint>
      <PasswordHint matches={hasSpecial}>
        {i18n.t('hasSpecial', { ns: 'profile' }) as string}
      </PasswordHint>
      <div className="relative">
        <Input
          id="password2"
          type={showRepeatedPassowrd ? 'text' : 'password'}
          name="password2"
          required={true}
          placeholder={i18n.t('passwordAgain', { ns: 'profile' })}
          autoComplete="new-password"
          value={password2}
          onChange={(e) => setPassword2(e.target.value)}
        />
        <LocalIcon
          iconName={showRepeatedPassowrd ? 'eye-off.svg' : 'eye.svg'}
          color={black}
          type="sm"
          style={{
            marginInlineStart: '0.5rem',
            cursor: 'pointer',
            position: 'absolute',
            left: '0.25rem',
            top: showRepeatedPassowrd ? '0.7rem' : '1rem',
          }}
          onClick={() => handleTogglePassword('reapeated_password')}
        />
      </div>
      <PasswordHint matches={matches}>
        {i18n.t('matches', { ns: 'profile' })}
      </PasswordHint>
      <Button
        type="submit"
        color="green"
        loading={loading}
        disabled={!allowSubmit}
      >
        <Icon className="right" type="save" style={{ float: 'right' }} />
        {i18n.t('save', { ns: 'profile' }) as string}
      </Button>
      <Button
        color="red"
        onClick={() => setChanging(false)}
        disabled={loading}
        style={{ float: 'left' }}
      >
        <Icon className="right" type="x" style={{ float: 'right' }} />
        {i18n.t('cancel', { ns: 'profile' }) as string}
      </Button>
    </PasswordForm>
  ) : changing2FA ? (
    <PasswordForm onSubmit={handleChange2FA}>
      <h6> {i18n.t('manage2fa', { ns: 'profile' }) as string}</h6>
      <p> {i18n.t('enterPasswordFor2FA', { ns: 'profile' }) as string}</p>
      <Input
        id="changePassword"
        type="password"
        name="changePassword"
        required={true}
        placeholder={i18n.t('password', { ns: 'profile' }) as string}
        value={password1}
        onChange={(e) => setPassword1(e.target.value)}
      />
      <Button
        type="submit"
        color="green"
        loading={loading}
        disabled={!password1}
      >
        <Icon className="right" type="save" style={{ float: 'right' }} />
        {userInfo.active2fa
          ? (i18n.t('disable2fa', { ns: 'profile' }) as string)
          : (i18n.t('enable2fa', { ns: 'profile' }) as string)}
      </Button>

      <Button
        color="red"
        onClick={() => setChanging2FA(false)}
        disabled={loading}
        style={{ float: 'left' }}
      >
        <Icon className="right" type="x" style={{ float: 'right' }} />
        {i18n.t('cancel', { ns: 'profile' }) as string}
      </Button>
    </PasswordForm>
  ) : (
    <div style={{}}>
      <h6> {i18n.t('managePassword', { ns: 'profile' }) as string} </h6>
      <Button
        onClick={() => {
          setChanging(true);
          changedHeight && changedHeight();
        }}
      >
        {userInfo!.hasPassword
          ? (i18n.t('changePassword', { ns: 'profile' }) as string)
          : (i18n.t('setPassword', { ns: 'profile' }) as string)}
      </Button>
      <h6>
        {(i18n.t('manage2fa', { ns: 'profile' }) as string) + ' : '}
        <span style={{ color: userInfo.active2fa ? blue : red }}>
          {userInfo.active2fa
            ? (i18n.t('active', { ns: 'profile' }) as string)
            : (i18n.t('inactive', { ns: 'profile' }) as string)}
        </span>
      </h6>
      {userInfo.active2fa ? (
        <div>
          <p>{i18n.t('yourUsing2fa', { ns: 'profile' }) as string}</p>
          <Button onClick={() => setChanging2FA(true)}>
            {i18n.t('disable2fa', { ns: 'profile' }) as string}
          </Button>
        </div>
      ) : userInfo.phone !== null /* && userInfo.hasPassword*/ ? (
        <Button onClick={() => setChanging2FA(true)}>
          {i18n.t('enable2fa', { ns: 'profile' }) as string}
        </Button>
      ) : (
        <QuestionHintBox>
          <HintIcon type="info" />
          <HintText>
            <span>
              {userInfo.phone === null
                ? (i18n.t('yourShouldSetPhoneFor2FA', {
                    ns: 'profile',
                  }) as string)
                : (i18n.t('yourShouldSetPasswordFor2FA', {
                    ns: 'profile',
                  }) as string)}
            </span>
          </HintText>
        </QuestionHintBox>
      )}
    </div>
  );
};

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state
) => {
  const thisUser = state.thisUser;
  const userInfo = state.makhsanUserInfo[Number(thisUser.userId)];
  return { userInfo, thisUser };
};

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => {
  return {
    updateUser: (
      deleted: boolean | null,
      firstName: string | null,
      lastName: string | null,
      password: string | null,
      active2FA: boolean | null,
      enable: boolean | null,
      userId: number
    ) => {
      return appDispatch(
        makhsanUpdateUser({
          deleted,
          firstName,
          lastName,
          password,
          active2fa: active2FA,
          enable,
          validIps: undefined,
          timeout: undefined,
          userId,
        })
      );
    },
    selfUserInfo: (userId: number) => {
      return appDispatch(makhsanSelfUserInfo({ userId }));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ChangePassword);
